import { format, parseISO } from 'date-fns';
import { Button, ButtonVariants } from '@components/button/Button';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { Closure } from '../apiHelpers';
import { toShortTimeOfDay } from '@shared/utils/dateFormatters';
import styles from '../shared/ModalListStyles.scss';
import typography from '~styles/typography.scss';
import { NO_DATA } from '@shared/constants/constants';
import cx from 'classnames';

interface UnblockReservationsModalProps {
  closeModal: () => void;
  onConfirm: () => void;
  isOpen: boolean;
  reservationDate: Date;
  closures: Closure[];
}

export const UnblockReservationsModal = ({
  closeModal,
  onConfirm,
  isOpen,
  closures,
}: UnblockReservationsModalProps) => {
  if (closures.length === 0) {
    return null;
  }

  const {
    startDate,
    endDate,
    firstReservationTime = '',
    lastReservationTime = '',
    guestFacingReason,
  } = closures[0];

  const formattedStart = format(parseISO(startDate), 'EEE MMM d, yyyy');
  const formattedEnd = format(parseISO(endDate), 'EEE MMM d, yyyy');
  const rangeText =
    startDate === endDate
      ? formattedStart
      : `${formattedStart} to ${formattedEnd}`;

  const formattedFirstReservationTime = firstReservationTime
    ? toShortTimeOfDay(firstReservationTime)
    : NO_DATA;
  const formattedLastReservationTime = lastReservationTime
    ? toShortTimeOfDay(lastReservationTime)
    : NO_DATA;

  return isOpen ? (
    <Modal isOpen={isOpen} onClose={closeModal} title="Unblock Reservations">
      <p className={cx(typography.c2, styles.modalSubtitle)}>
        Remove the block on new online reservations set for {rangeText}.
      </p>

      {guestFacingReason ? (
        <div className={styles.listContainer}>
          <p className={styles.listLabel}>Guest-Facing Reason</p>
          <p className={typography.t1}>{guestFacingReason}</p>
          <p className={typography.c2}>
            This message is currently visible to guests and will be removed when
            reservations are unblocked.
          </p>
        </div>
      ) : null}
      <div className={styles.listContainer}>
        <p className={styles.listLabel}>Blocked Reservations</p>
        <ul className={styles.list}>
          <li className={styles.listItem}>
            <p className={styles.listItemDetails}>
              First Reservable Time:{' '}
              <span className={typography.t2}>
                {formattedFirstReservationTime}
              </span>
            </p>
          </li>
          <li className={styles.listReservationItem}>
            <p className={styles.listItemDetails}>
              Last Reservable Time:{' '}
              <span className={typography.t2}>
                {formattedLastReservationTime}
              </span>
            </p>
          </li>
        </ul>
      </div>
      <ModalActions>
        <Button
          label="Cancel"
          onClick={closeModal}
          variant={ButtonVariants.Tertiary}
        />
        <Button
          label="Unblock Reservations"
          onClick={onConfirm}
          variant={ButtonVariants.Primary}
        />
      </ModalActions>
    </Modal>
  ) : null;
};
